import { useCallback } from 'react';
import {
  type TranslationFnc, useTranslation,
} from '~/_shared/utils/hooks';
import { memoizeWeak } from '~/_shared/utils/memoize/memoize';
import { isBoundaryTerritorySpecial } from '~/boundary/settings/boundarySettings.helpers';
import { useGetBoundaryTerritoryDisplayNames } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useGetBoundaryTerritoryDisplayName';
import {
  type BoundaryId, type BoundaryTerritoryId,
} from '~/store/boundaries/boundaryIdentifier.type';
import { type BoundaryStateItem } from '~/store/boundaryItems/boundaryItems.state';
import { getBoundaryLabelValueCallback } from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.helpers';
import {
  type BoundaryTerritoryAssignment,
  type BoundaryTerritoryCountsAndExtraData,
  useBoundaryTerritoryAssignments,
  useBoundaryTerritoryExtraData,
} from '~/store/boundaryTerritoryDetails/boundaryTerritoryDetails.selectors';
import { type BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import {
  type MapBoundaryLabelLegendCallback, useMapBoundaryLabelLegend,
} from './useMapBoundaryLabelLegend';

export type MapBoundaryLabelContentCallback = (boundary: BoundaryStateItem) => {
  boundaryName: string | null;
  boundaryValue: string | null;
  boundaryValueLegend: string | null;
};

export const useMapBoundaryLabelContent = () => {
  const boundaryTerritoryAssignments = useBoundaryTerritoryAssignments();
  const boundaryTerritoryExtraData = useBoundaryTerritoryExtraData();
  const { boundaryTerritoryDisplayNames } = useGetBoundaryTerritoryDisplayNames();
  const { getMapBoundaryLabelLegend } = useMapBoundaryLabelLegend();
  const [t] = useTranslation();

  const createLabelContentCallback = useCallback((boundaryTerritoryGroup: BoundaryTerritoryGroup): MapBoundaryLabelContentCallback => {
    return getBoundaryLabelTextMemoized(
      boundaryTerritoryGroup,
      boundaryTerritoryAssignments.get(boundaryTerritoryGroup.boundaryTerritoryGroupId),
      boundaryTerritoryDisplayNames.get(boundaryTerritoryGroup.boundaryTerritoryGroupId),
      boundaryTerritoryExtraData.get(boundaryTerritoryGroup.boundaryTerritoryGroupId),
      getMapBoundaryLabelLegend,
      t,
    );
  }, [boundaryTerritoryAssignments, boundaryTerritoryDisplayNames, boundaryTerritoryExtraData, getMapBoundaryLabelLegend, t]);

  return { createLabelContentCallback };
};

const getBoundaryLabelText = (
  boundaryTerritoryGroup: BoundaryTerritoryGroup,
  boundaryTerritoryAssignment: BoundaryTerritoryAssignment | undefined,
  boundaryTerritoryDisplayNames: ReadonlyMap<BoundaryTerritoryId, string> | undefined,
  boundaryExtraData: BoundaryTerritoryCountsAndExtraData | undefined,
  getMapBoundaryLabelLegend: MapBoundaryLabelLegendCallback,
  t: TranslationFnc,
) => {
  const { settings } = boundaryTerritoryGroup;
  const { labelValueConfig: { showValue, includeLegend }, hideLabels } = settings;

  // Get value callback based on boundary territory group settings
  const boundaryValueCallback = boundaryExtraData ? getBoundaryLabelValueCallback(boundaryTerritoryGroup, boundaryExtraData, t) : null;
  const boundaryValueLegend = getMapBoundaryLabelLegend(boundaryTerritoryGroup);

  const getTextFromBoundaryTerritory = (boundaryId: BoundaryId) => {
    const boundaryTerritoryId = boundaryTerritoryAssignment?.boundaryAssignments.get(boundaryId);
    const boundaryTerritory = settings.boundaryTerritories.find(bt => bt.boundaryTerritoryId === boundaryTerritoryId);

    if (!boundaryTerritory || isBoundaryTerritorySpecial(boundaryTerritory)) {
      return null;
    }

    return boundaryTerritoryDisplayNames?.get(boundaryTerritory.boundaryTerritoryId) ?? null;
  };

  return (boundary: BoundaryStateItem) => {
    const boundaryName = settings.boundariesAsTerritories ? getTextFromBoundaryTerritory(boundary.id) ?? boundary.displayName : boundary.displayName;
    const boundaryValue = boundaryValueCallback ? boundaryValueCallback(boundary.id) : null;

    return {
      boundaryName: hideLabels ? null : boundaryName,
      boundaryValue: showValue ? boundaryValue : null,
      boundaryValueLegend: includeLegend ? boundaryValueLegend : null,
    };
  };
};

const getBoundaryLabelTextMemoized = memoizeWeak(getBoundaryLabelText);
